import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import Select from 'react-select';
import {
    Building2,
    Store,
    Phone,
    Calendar,
    Check,
    X,
    Filter,
    ChevronDown,
    AlertCircle,
    Milestone
} from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { Button } from '../../components/ui/Button';
import { Dialog, DialogFooter } from '../../components/ui/Dialog';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import {
    fetchKycPendingMerchantsThunk,
    setPage,
    setPageSize,
    setSortBy,
    setSortOrder,
    setSearchQuery,
    setFilters,
    resetFilters,
} from '../../features/kycPendingMerchants/merchantKYCSlice';
import { Merchant, updateMerchantByMerchantId, updateMerchant, getPartnersList, deleteDocument } from '../../services/merchant';
import { UserRole } from '../../types/auth';
import MerchantDetailModal from './MerchantDetailsModal';
import { validateCustomerSearchInput } from "../../utils/searchValidator";
import { showToast } from '../../utils/toast';


// Filter type
interface MerchantFilters {
    group: string;
    kycStatus: 'all' | 'Verified' | 'NotVerified' | 'Not Verified';
    dateRange: {
        from: string | undefined;
        to: string | undefined;
    };
}

const EnhancedMerchantsPage: React.FC = () => {

    const dispatch = useAppDispatch();

    const { token, user } = useAppSelector(state => state.auth);
    const {
        merchants,
        loading,
        page,
        pageSize,
        total,
        sortBy,
        sortOrder,
        searchQuery,
        filters,
        hasFetched,
    } = useAppSelector(state => state.merchantKYC);

    const currentUserRole = user?.role || UserRole.CLEVERPE_USER;

    // Check if user can manage merchants (needs only the manage-merchant permission)
    const canManageMerchants = useMemo(() => {
        // Check if user has the specific manage-merchant permission
        const hasManageMerchantsPermission = user?.dashPermissions?.includes('manage-merchant') || false;

        // Only check for the permission, regardless of user role
        return hasManageMerchantsPermission;
    }, [user?.dashPermissions]);



    const authToken = localStorage.getItem("token") || "";

    const headers = {
        "Content-Type": "application/json",
        token: authToken,
    };



    const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

    // useEffect(() => {
    //     dispatch(
    //         setFilters({
    //             ...filters,
    //             kycStatus: 'KycPending',
    //         })
    //     );
    // }, []);


    const prevRef = useRef({
        searchQuery,
        filters,
        page,
        pageSize,
        sortBy,
        sortOrder,
    });

    const [localSearch, setLocalSearch] = useState("");
    const [searchError, setSearchError] = useState<string | null>(null);
    const [isSearchValid, setIsSearchValid] = useState(false);
    const handleSearchInputChange = (value: string) => {
        setLocalSearch(value);

        // ✅ When input is cleared
        if (!value.trim()) {
            setSearchError(null);
            setIsSearchValid(true); // allow reset

            // 🔁 Reset table immediately
            dispatch(setPage(1));
            dispatch(setSearchQuery("")); // empty search
            return;
        }

        // Normal validation
        const result = validateCustomerSearchInput(value);
        setIsSearchValid(result.isValid);
        setSearchError(result.message || null);
    };


    const handleSearchSubmit = () => {
        // If empty, already handled in change
        if (!localSearch.trim()) return;

        if (!isSearchValid) return;

        dispatch(setPage(1));
        dispatch(setSearchQuery(localSearch));
    };

    const handleResetSearch = () => {
        setLocalSearch("");    // clears input
        setSearchError(null);  // clears error
        setIsSearchValid(false); // disables search button
    };

    // 🔹 First load — only once per session
    useEffect(() => {
        if (!token) return;
        if (hasFetched) return; // ✅ Already fetched, skip

        // Reset filters for KYC Pending page
        dispatch(
            setFilters({
                group: '',
                kycStatus: 'KycPending',
                dateRange: { from: undefined, to: undefined },
            })
        );
        dispatch(setPage(1));

        // Trigger API immediately
        dispatch(fetchKycPendingMerchantsThunk());

        // Save snapshot for later change detection
        prevRef.current = {
            searchQuery: '',
            filters: { group: '', kycStatus: 'KycPending', dateRange: { from: undefined, to: undefined } },
            page: 1,
            pageSize,
            sortBy,
            sortOrder,
        };
    }, [token, dispatch, hasFetched, pageSize, sortBy, sortOrder]);

    // 🔹 Watch for user actions — filters/search/pagination/sort
    useEffect(() => {
        if (!hasFetched) return; // wait until first load
        const prev = prevRef.current;

        const hasChanged =
            prev.searchQuery !== searchQuery ||
            prev.page !== page ||
            prev.pageSize !== pageSize ||
            prev.sortBy !== sortBy ||
            prev.sortOrder !== sortOrder ||
            prev.filters.group !== filters.group ||
            prev.filters.kycStatus !== filters.kycStatus ||
            prev.filters.dateRange?.from !== filters.dateRange?.from ||
            prev.filters.dateRange?.to !== filters.dateRange?.to;

        if (!hasChanged) return;

        const timeout = setTimeout(() => {
            dispatch(fetchKycPendingMerchantsThunk());

            // Update snapshot AFTER dispatch
            prevRef.current = {
                searchQuery,
                filters: { ...filters, dateRange: { ...filters.dateRange } },
                page,
                pageSize,
                sortBy,
                sortOrder,
            };
        }, 500);

        return () => clearTimeout(timeout);
    }, [
        searchQuery,
        filters.group,
        filters.kycStatus,
        filters.dateRange?.from,
        filters.dateRange?.to,
        page,
        pageSize,
        sortBy,
        sortOrder,
        dispatch,
        hasFetched,
    ]);





    // Filter state
    const [showFilters, setShowFilters] = useState(false);

    // Modal state
    const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Confirmation modals state
    const [showMerchantConfirm, setShowMerchantConfirm] = useState(false);
    const [showDocumentConfirm, setShowDocumentConfirm] = useState(false);
    const [pendingStatusMerchant, setPendingStatusMerchant] = useState<Merchant | null>(null);
    const [pendingDeleteDocument, setPendingDeleteDocument] = useState<{ merchantMobile: string, docType: string } | null>(null);



    // Request to update merchant status - stores merchant and shows confirmation
    const requestMerchantStatusUpdate = (merchant: Merchant) => {
        console.log('[CONFIRM] Requesting merchant status change confirmation:', merchant.name);
        setPendingStatusMerchant(merchant);
        setShowMerchantConfirm(true);
    };

    // Request to delete document - stores document info and shows confirmation
    const requestDocumentDeletion = (merchantMobile: string, docType: string) => {
        console.log('[CONFIRM] Requesting document deletion confirmation');
        setPendingDeleteDocument({ merchantMobile, docType });
        setShowDocumentConfirm(true);
    };

    // Update merchant status
    const handleMerchantStatusUpdate = async (merchant: Merchant) => {
        if (!merchant || !merchant._id || !token) {
            showToast.error('Missing required information for merchant action');
            return;
        }

        setIsUpdating(true);
        console.log('[DIRECT ACTION] Starting merchant status update for:', {
            id: merchant._id,
            name: merchant.name,
            mobile: merchant.mobileNo,
            currentStatus: {
                suspended: merchant.suspended,
                activated: merchant.activated
            }
        });

        try {
            // Create update data - toggle suspended and activated states
            const updateData: Partial<Merchant> = {
                suspended: !merchant.suspended,
                activated: merchant.suspended // If suspended=true, we set activated=false and vice versa
            };

            console.log('[DIRECT ACTION] Update data:', updateData);

            // Call the update API with merchant ID
            let success = false;

            if (merchant.merchantId) {
                success = await updateMerchantByMerchantId(merchant.merchantId, updateData, token);
            } else {
                success = await updateMerchant(merchant.mobileNo, updateData, token);
            }

            if (success) {
                const actionText = merchant.suspended ? 'reactivated' : 'terminated';
                showToast.success(`Merchant ${merchant.name || merchant.merchantId} ${actionText} successfully`);
                // Close any open dialogs
                setShowMerchantConfirm(false);
                setDetailsOpen(false);
                setSelectedMerchant(null);
                setPendingStatusMerchant(null);
                // Refresh the data
                await fetchKycPendingMerchantsThunk();
            } else {
                showToast.error(`Failed to ${merchant.suspended ? 'reactivate' : 'terminate'} merchant`);
            }
        } catch (error: any) {
            console.error('[DIRECT ACTION] Error:', error);
            showToast.error(error.message || `Error ${merchant.suspended ? 'reactivating' : 'terminating'} merchant`);
        } finally {
            setIsUpdating(false);
        }
    };

    // Simplified direct document delete function without confirmation (used by confirmation dialog)
    const handleDirectDocumentDelete = async (merchantId: string, docType: string) => {
        if (!merchantId || !docType || !token) {
            showToast.error('Missing required information for document deletion');
            return;
        }

        setIsUpdating(true);
        console.log('[DIRECT DELETE] Starting document deletion:', {
            merchantId,
            documentType: docType
        });

        try {
            // Call the API directly with document info
            const success = await deleteDocument(merchantId, docType, token);

            if (success) {
                showToast.success(`Document deleted successfully`);

                // Find the target merchant first
                const targetMerchant = merchants.find(m => m.mobileNo === merchantId);

                // After successful document deletion, update merchant's KYC status to false
                const updateData = {
                    kyc: {
                        status: false,
                        docs: false,
                        bank: targetMerchant?.kyc?.bank || false, // Use targetMerchant instead of merchant
                        shop: targetMerchant?.kyc?.shop || false  // Use targetMerchant instead of merchant
                    }
                };

                // Update merchant's KYC status
                let kycUpdateSuccess = false;

                if (targetMerchant?.merchantId) {
                    kycUpdateSuccess = await updateMerchantByMerchantId(
                        targetMerchant.merchantId,
                        updateData,
                        token
                    );
                } else {
                    kycUpdateSuccess = await updateMerchant(merchantId, updateData, token);
                }

                if (kycUpdateSuccess) {
                    console.log('[DIRECT DELETE] Successfully updated merchant KYC status to false');
                } else {
                    console.error('[DIRECT DELETE] Failed to update merchant KYC status');
                }

                // Close confirmation modal
                setShowDocumentConfirm(false);
                setPendingDeleteDocument(null);
                // Refresh the data
                await fetchKycPendingMerchantsThunk();
            } else {
                showToast.error('Failed to delete document');
            }
        } catch (error: any) {
            console.error('[DIRECT DELETE] Document delete error:', error);
            showToast.error(error.message || 'Error deleting document');
        } finally {
            setIsUpdating(false);
        }
    };

    // Execute merchant status update from confirmation dialog
    const confirmMerchantStatusUpdate = () => {
        if (pendingStatusMerchant) {
            handleMerchantStatusUpdate(pendingStatusMerchant);
        } else {
            console.error('[CONFIRM] No merchant to update');
            setShowMerchantConfirm(false);
        }
    };

    // Available groups/brands for filter
    const [availableGroups, setAvailableGroups] = useState<string[]>([]);

    // Fetch all merchant groups on component mount
    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const partners = await getPartnersList();

                // If role is cleverpeuser, filter by user.groups
                if (
                    user?.role === UserRole.CLEVERPE_USER &&
                    Array.isArray(user?.groups)
                ) {
                    const filteredPartners = partners.filter(p =>
                        user.groups!.includes(p)
                    );
                    setAvailableGroups(filteredPartners);
                } else {
                    setAvailableGroups(partners);
                }
            } catch (error) {
                console.error('Error fetching partner groups:', error);
            }
        };
        fetchGroups();
    }, [user]);

    const isCleverUser = user?.role === UserRole.CLEVERPE_USER;
    const hasSearchOrFilters =
        searchQuery.trim() !== '' ||
        !!filters.group ||
        !!filters.dateRange?.from ||
        !!filters.dateRange?.to




    const handleRefresh = () => {
        dispatch(setPage(1));
        dispatch(fetchKycPendingMerchantsThunk());
    };




    // Format date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    // 1️⃣ Extend Merchant with local stage
    interface MerchantWithKycStage extends Merchant {
        kyc: Merchant['kyc'] & {
            stage?: string;
        };
    }

    // 2️⃣ Helper to calculate stage
    const getKycStage = (kyc: Merchant['kyc']): string => {
        if (kyc.bank === false) return 'On Bank';
        if (kyc.shop === false) return 'On GSTIN';
        if (kyc.docs === false) return 'On Document Upload';
        return 'Completed';
    };



    // 4️⃣ Columns
    const columns: ColumnDef<MerchantWithKycStage>[] = [
        {
            id: 'serialNo',
            header: 'S.No',
            cell: ({ row }) => (
                <div className="text-gray-600">
                    {row.original.serialNumber || ((page - 1) * pageSize + row.index + 1)}
                </div>
            ),
            enableSorting: false,
            size: 30,
            meta: { width: '30px' }
        },
        {
            accessorKey: "name",
            header: "Merchant",
            cell: ({ row }) => (
                <div className="flex items-start gap-3">
                    <Store className="w-4 h-4 text-gray-500 mt-1" />

                    <div className="flex flex-col gap-1">
                        {/* Name */}
                        <span className="font-medium text-gray-900">
                            {row.original.name}
                        </span>

                        {/* ID */}
                        <span className="text-sm text-gray-500">
                            {row.original.merchantId}
                        </span>

                        {/* Status */}
                        <span
                            className={`inline-flex w-fit items-center px-2 py-0.5 rounded-full text-xs font-medium
            ${row.original.suspended
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                        >
                            {row.original.suspended ? "Suspended" : "Active"}
                        </span>
                    </div>
                </div>
            ),
            enableSorting: true,
        },
        {
            accessorKey: "mobileNo",
            header: "Contact",
            cell: ({ row }) => (
                <div className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-gray-500 mt-1" />

                    <div className="flex flex-col gap-1">
                        {/* Mobile */}
                        <span className="text-sm font-medium text-gray-900">
                            {row.original.mobileNo}
                        </span>

                        {/* Email */}
                        <span className="text-sm text-gray-500">
                            {row.original.email}
                        </span>
                    </div>
                </div>
            ),
            enableSorting: true,
        },
        {
            accessorKey: 'afpId',
            header: 'Group',
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-500" />
                    <span>{row.original.afpId}</span>
                </div>
            ),
            enableSorting: true,
        },
        {
            accessorKey: "kyc.status",
            header: "KYC Status",
            cell: ({ row }) => {
                const isVerified = row.original.kyc.status;

                return (
                    <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
          ${isVerified
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                    >
                        {isVerified ? (
                            <Check className="w-3.5 h-3.5" />
                        ) : (
                            <X className="w-3.5 h-3.5" />
                        )}
                        {isVerified ? "Verified" : "Not Verified"}
                    </span>
                );
            },
            enableSorting: true,
        }

    ];

    // 5️⃣ Add final column conditionally
    if (isCleverUser) {
        columns.push({
            accessorKey: 'kyc.stage',
            header: 'KYC Stage',
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <Milestone className="w-4 h-4 text-gray-500" />
                    <span>{row.original.kyc.stage || 'N/A'}</span>
                </div>
            ),
            enableSorting: true,
        });
    } else {
        columns.push({
            accessorKey: "createdAt",
            header: "",
            cell: ({ row }) => (
                <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-gray-500 mt-1" />

                    <div className="flex flex-col gap-1 text-sm">
                        {/* Created At */}
                        <div className="flex items-center gap-1">
                            <span className="text-gray-500">Created:</span>
                            <span className="text-gray-900">
                                {formatDate(row.original.createdAt)}
                            </span>
                        </div>

                        {/* Last Login */}
                        <div className="flex items-center gap-1">
                            <span className="text-gray-500">Last login:</span>
                            <span className="text-gray-700">
                                {formatDate(row.original.lastLogin)}
                            </span>
                        </div>
                    </div>
                </div>
            ),
            enableSorting: true,
        });

    }

    // Handle row click
    const handleRowClick = (merchant: Merchant) => {
        setSelectedMerchant(merchant);
        setDetailsOpen(true);
    };

    // Handle closing details dialog
    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedMerchant(null);
    };

    // Handle merchant update
    const handleMerchantUpdate = async () => {
        // Refresh merchants data
        await fetchKycPendingMerchantsThunk();
    };

    // Handle filter changes
    const handleFilterChange = (key: keyof MerchantFilters, value: any) => {
        if (key === 'dateRange') {
            dispatch(
                setFilters({
                    ...filters,
                    dateRange: {
                        ...filters.dateRange,
                        ...value,
                    },
                })
            );
        } else {
            dispatch(
                setFilters({
                    ...filters,
                    [key]: value,
                })
            );
        }
    };


    // Handle filter reset
    const handleResetFilters = () => {
        dispatch(
            setFilters({
                group: '',
                kycStatus: 'KycPending',
                dateRange: { from: undefined, to: undefined }
            })
        );
        dispatch(setPage(1)); // reset page
        dispatch(fetchKycPendingMerchantsThunk()); // trigger API immediately
    };


    // Toggle filter visibility
    const toggleFilters = () => {
        setShowFilters(prev => !prev);
    };

    // Execute document deletion from confirmation dialog
    const confirmDocumentDeletion = () => {
        if (pendingDeleteDocument) {
            const { merchantMobile, docType } = pendingDeleteDocument;
            handleDirectDocumentDelete(merchantMobile, docType);
        } else {
            console.error('[CONFIRM] No document to delete');
            setShowDocumentConfirm(false);
        }
    };


    return (
        <div className="w-full no-scrollbar">
            {/* Filters and search section */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4 space-x-4">
                    {/* Filters Button - Always visible */}
                    <Button
                        variant="outline"
                        onClick={toggleFilters}
                        className="flex items-center"
                        size="sm"
                    >
                        <Filter size={14} className="mr-1" />
                        Filters
                        <ChevronDown size={14} className="ml-1" />
                    </Button>

                </div>

                {/* Filter controls */}
                {showFilters && (
                    <div className="p-4 mb-4 bg-white border border-gray-200 rounded-lg shadow-sm no-scrollbar">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            {/* Partner/Group filter with React Select */}
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                    Partner/Group
                                </label>
                                <Select
                                    value={filters.group ? { value: filters.group, label: filters.group } : null}
                                    onChange={(option) => handleFilterChange('group', option ? option.value : '')}
                                    options={availableGroups.map(group => ({ value: group, label: group }))}
                                    isClearable
                                    placeholder="Search or select partner..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    styles={{
                                        control: (baseStyles) => ({
                                            ...baseStyles,
                                            borderColor: '#D1D5DB',
                                            borderRadius: '0.5rem',
                                            minHeight: '42px',
                                            boxShadow: 'none'
                                        }),
                                        placeholder: (baseStyles) => ({
                                            ...baseStyles,
                                            color: '#9CA3AF'
                                        }),
                                        option: (baseStyles, state) => ({
                                            ...baseStyles,
                                            backgroundColor: state.isSelected ? '#E0E7FF' :
                                                state.isFocused ? '#F3F4F6' : 'transparent',
                                            color: state.isSelected ? '#4F46E5' : '#374151',
                                            ':active': {
                                                backgroundColor: '#E0E7FF'
                                            }
                                        })
                                    }}
                                />
                            </div>

                            {/* Date range filter */}
                            {user?.role !== UserRole.CLEVERPE_USER && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block mb-1 text-sm font-medium text-gray-700">
                                            From Date
                                        </label>
                                        <input
                                            type="date"
                                            value={filters.dateRange.from || ''}
                                            onChange={(e) => handleFilterChange('dateRange', { from: e.target.value || undefined })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-1 text-sm font-medium text-gray-700">
                                            To Date
                                        </label>
                                        <input
                                            type="date"
                                            value={filters.dateRange.to || ''}
                                            onChange={(e) => handleFilterChange('dateRange', { to: e.target.value || undefined })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Filter actions */}
                        <div className="flex justify-end mt-4">
                            <Button
                                variant="outline"
                                onClick={handleResetFilters}
                                size="sm"
                                className="mr-2"
                            >
                                Reset
                            </Button>
                            <Button
                                onClick={() => {
                                    dispatch(fetchKycPendingMerchantsThunk());
                                }}
                                size="sm"
                            >
                                Apply Filters
                            </Button>

                        </div>
                    </div>
                )}
            </div>

            {/* Merchants Table */}
            <div className="bg-white rounded-lg shadow-sm">
                <CommonTable
                    columns={columns}
                    data={
                        !isCleverUser || hasSearchOrFilters
                            ? merchants // ✅ Show data
                            : []        // ❌ Hide unless filters/search
                    }
                    isLoading={loading}
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    onPageChange={(p) => dispatch(setPage(p))}
                    onPageSizeChange={(size) => {
                        dispatch(setPage(1));          // 🔑 ADD THIS
                        dispatch(setPageSize(size));
                    }}
                    onSortChange={(field, order) => {
                        dispatch(setPage(1));          // 🔑 ADD THIS
                        dispatch(setSortBy(field));
                        dispatch(setSortOrder(order));
                    }}

                    searchValue={localSearch}
                    onSearchChange={handleSearchInputChange}
                    onSearchSubmit={handleSearchSubmit}
                    isSearchDisabled={!isSearchValid}
                    searchError={searchError}
                    onResetSearch={handleResetSearch} // ✅ new

                    onRefresh={handleRefresh}
                    searchPlaceholder="Search merchants..."
                    enableRowClick={!isCleverUser} // ✅ Disable row click for clever user
                    onRowClick={!isCleverUser ? handleRowClick : undefined}
                    emptyStateMessage={
                        isCleverUser && !hasSearchOrFilters
                            ? 'Please apply search or filter to view merchants.'
                            : 'No merchants found.'
                    }
                />
            </div>


            {/* Merchant Details Modal */}
            {selectedMerchant && (
                <MerchantDetailModal
                    isOpen={detailsOpen}
                    onClose={handleCloseDetails}
                    merchant={selectedMerchant}
                    canManage={canManageMerchants}
                    onDelete={() => {
                        console.log('[DIRECT ACTION] Status change button clicked in merchant detail modal');
                        requestMerchantStatusUpdate(selectedMerchant);
                    }}
                    onDocumentDelete={(docType: string) => {
                        console.log('[DIRECT DELETE] Document delete button clicked in merchant detail modal');
                        requestDocumentDeletion(selectedMerchant.mobileNo, docType);
                    }}
                    onUpdate={handleMerchantUpdate}
                />
            )}

            {/* Merchant Status Change Confirmation Modal */}
            {pendingStatusMerchant && (
                <Dialog
                    isOpen={showMerchantConfirm}
                    onClose={() => {
                        if (!isUpdating) {
                            setShowMerchantConfirm(false);
                            setPendingStatusMerchant(null);
                        }
                    }}
                    title={pendingStatusMerchant.suspended ? "Confirm Merchant Reactivation" : "Confirm Merchant Termination"}
                    size="sm"
                    preventClose={isUpdating}
                >
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            {pendingStatusMerchant.suspended ? (
                                <>Are you sure you want to reactivate merchant "<strong>{pendingStatusMerchant.name || pendingStatusMerchant.merchantId}</strong>"?</>
                            ) : (
                                <>Are you sure you want to terminate merchant "<strong>{pendingStatusMerchant.name || pendingStatusMerchant.merchantId}</strong>"?</>
                            )}
                        </p>
                        <div className={`p-3 rounded-md border ${pendingStatusMerchant.suspended ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                            <div className="flex items-start">
                                <AlertCircle size={16} className={`mr-2 mt-0.5 flex-shrink-0 ${pendingStatusMerchant.suspended ? 'text-green-800' : 'text-amber-800'}`} />
                                <div>
                                    <p className={`text-sm ${pendingStatusMerchant.suspended ? 'text-green-800' : 'text-amber-800'}`}>
                                        {pendingStatusMerchant.suspended
                                            ? "The merchant will be reactivated and can resume operations."
                                            : "The merchant will be suspended and will not be able to perform any operations."}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end pt-4 space-x-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowMerchantConfirm(false);
                                    setPendingStatusMerchant(null);
                                }}
                                disabled={isUpdating}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant={pendingStatusMerchant.suspended ? "secondary" : "danger"}
                                className={pendingStatusMerchant.suspended ? "bg-green-600 text-white hover:bg-green-700" : ""}
                                isLoading={isUpdating}
                                onClick={confirmMerchantStatusUpdate}
                                disabled={isUpdating}
                            >
                                {isUpdating
                                    ? (pendingStatusMerchant.suspended ? 'Reactivating...' : 'Terminating...')
                                    : (pendingStatusMerchant.suspended ? 'Reactivate Merchant' : 'Terminate Merchant')}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            )}

            {/* Document Delete Confirmation Modal */}
            {pendingDeleteDocument && (
                <Dialog
                    isOpen={showDocumentConfirm}
                    onClose={() => {
                        if (!isUpdating) {
                            setShowDocumentConfirm(false);
                            setPendingDeleteDocument(null);
                        }
                    }}
                    title="Confirm Document Deletion"
                    size="sm"
                    preventClose={isUpdating}
                >
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            Are you sure you want to delete this document? Once deleted, the merchant will have to reupload it.
                        </p>
                        <div className="flex justify-end pt-4 space-x-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowDocumentConfirm(false);
                                    setPendingDeleteDocument(null);
                                }}
                                disabled={isUpdating}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                isLoading={isUpdating}
                                onClick={confirmDocumentDeletion}
                                disabled={isUpdating}
                            >
                                {isUpdating ? 'Deleting...' : 'Delete Document'}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default EnhancedMerchantsPage;